import BaseFormatter from './base'
import { SignalVerdict } from '../../types/signal'
import { castVerdictToStrength } from '../../helpers/signal-type'
import {
  LlmDynamicProbesData,
  LlmProbeCoverage,
  LlmProbeJudgeHit,
  LlmProbeSession,
  LlmProbeText,
} from '../../features/llm-dynamic-probes/types'

// Finding severity -> verdict, mirroring the strength the dynamic LLM consumers assign to the signal
const severityVerdicts: { [severity: string]: SignalVerdict } = {
  critical: SignalVerdict.Malicious,
  high: SignalVerdict.Likely_malicious,
  medium: SignalVerdict.Suspicious,
  low: SignalVerdict.No_threat,
}

/**
 * Prepare the dynamic LLM scan report (`llm-scan-dynamic` resource, `dynamicScan` key)
 */
export default class LlmDynamicProbesFormatter extends BaseFormatter {
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    const resource = this.getResource(data, 'llm-scan-dynamic')

    if (!resource) {
      return
    }

    const errorMessages: string[] = Array.isArray(resource.errorMessages) ? resource.errorMessages : []

    // A scan that fails outright writes only errorMessages, and those still belong on the tab
    if (!resource.dynamicScan && !errorMessages.length) {
      return
    }

    const scan = resource.dynamicScan ?? {}
    const findings: { [key: string]: any }[] = Array.isArray(scan.findings) ? scan.findings : []
    const usedIds = new Set<string>()
    const sessions = findings
      .map((finding, idx) => this.formatSession(finding, idx, usedIds))
      .sort(
        (a, b) =>
          castVerdictToStrength(b.verdict) - castVerdictToStrength(a.verdict) ||
          (b.confidence ?? 0) - (a.confidence ?? 0)
      )

    const result: LlmDynamicProbesData = {
      model: scan.model ?? undefined,
      modelAlias: scan.modelAlias ?? undefined,
      harnessAddress: scan.harnessAddress ?? undefined,
      durationSeconds: this.toNumber(scan.durationSeconds),
      tokensIn: this.toNumber(scan.tokensIn),
      tokensOut: this.toNumber(scan.tokensOut),
      coverage: this.formatCoverage(scan.coverage),
      findingsCount: this.toNumber(scan.findingsCount) ?? sessions.length,
      sessions,
      errorMessages,
    }

    report.llmDynamicProbes = result
  }

  formatSession(finding: { [key: string]: any }, idx: number, usedIds: Set<string>): LlmProbeSession {
    const ruleId: string = finding.ruleId || `probe-${idx + 1}`
    const promptId: string | undefined = finding.promptId || undefined
    let id = promptId ? `${ruleId}:${promptId}` : `${ruleId}:${idx}`
    while (usedIds.has(id)) {
      id = `${id}:${idx}`
    }
    usedIds.add(id)

    // Every prompt of one probe repeats the rule id, so the tail of the prompt id is what tells sessions apart
    const promptTail = promptId?.startsWith(ruleId) ? promptId.slice(ruleId.length).replace(/^[-_]/, '') : promptId

    return {
      id,
      ruleId,
      title: finding.subtechnique || finding.vector || ruleId,
      promptCase: promptTail || undefined,
      vector: finding.vector || undefined,
      subtechnique: finding.subtechnique || undefined,
      atlasTechniques: Array.isArray(finding.atlasTechniques) ? finding.atlasTechniques : [],
      severity: finding.severity || undefined,
      verdict: severityVerdicts[String(finding.severity ?? '').toLowerCase()] ?? SignalVerdict.Unknown,
      confidence: this.toNumber(finding.finalConfidence),
      promptId,
      remediation: finding.remediation || undefined,
      prompt: this.formatText(finding.prompt),
      response: this.formatText(finding.response),
      hits: (Array.isArray(finding.hits) ? finding.hits : [])
        .map((hit: { [key: string]: any }) => this.formatHit(hit))
        // A judge that scored zero did not contribute: the refusal judge matches a plain apology inside an
        // otherwise compliant answer, and marking that as evidence would contradict the response on screen
        .filter((hit: LlmProbeJudgeHit) => hit.confidence > 0),
      conversationId: finding.conversationId || undefined,
      latencyMs: this.toNumber(finding.latencyMs),
      sentAt: finding.sentAt || undefined,
      receivedAt: finding.receivedAt || undefined,
    }
  }

  // Results written before the DTO rework carry prompt/response as plain strings
  formatText(value: any): LlmProbeText | undefined {
    if (typeof value === 'string') {
      return { text: value, truncated: false }
    }
    if (!value || typeof value.text !== 'string') {
      return undefined
    }

    return { text: value.text, truncated: !!value.truncated }
  }

  formatHit(hit: { [key: string]: any }): LlmProbeJudgeHit {
    return {
      judge: hit.judge || 'judge',
      confidence: this.toNumber(hit.confidence) ?? 0,
      rule: hit.rule || undefined,
      matches: (Array.isArray(hit.matches) ? hit.matches : []).filter(
        (match: unknown) => typeof match === 'string' && match.length > 0
      ),
      why: hit.why || undefined,
    }
  }

  // Outcome counters are sparse in the scanner output: an outcome that never happened has no key
  formatCoverage(coverage: { [key: string]: any } | undefined): LlmProbeCoverage | undefined {
    if (!coverage) {
      return undefined
    }

    const probes = coverage.probes ?? {}
    const prompts = coverage.prompts ?? {}
    const outcome = prompts.outcome ?? {}

    return {
      probes: {
        selected: this.toNumber(probes.selected) ?? 0,
        measured: this.toNumber(probes.measured) ?? 0,
        partial: this.toNumber(probes.partial) ?? 0,
        unmeasured: this.toNumber(probes.unmeasured) ?? 0,
        notApplicable: this.toNumber(probes.notApplicable) ?? 0,
        noPrompts: this.toNumber(probes.noPrompts) ?? 0,
      },
      prompts: {
        planned: this.toNumber(prompts.planned) ?? 0,
        sent: this.toNumber(prompts.sent) ?? 0,
        answered: this.toNumber(prompts.answered) ?? 0,
        notSent: this.toNumber(prompts.notSent) ?? 0,
        noAnswer: this.toNumber(prompts.noAnswer) ?? 0,
        outcome: {
          declined: this.toNumber(outcome.declined) ?? 0,
          affected: this.toNumber(outcome.affected) ?? 0,
          noSignal: this.toNumber(outcome.noSignal) ?? this.toNumber(outcome.no_signal) ?? 0,
        },
      },
      truncated: !!coverage.truncated,
    }
  }

  toNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined
    }

    // A blank string converts to 0, which would read as a real counter rather than a missing one
    if (typeof value === 'string' && value.trim()) {
      const num = Number(value)

      return Number.isFinite(num) ? num : undefined
    }

    return undefined
  }
}
